/**
 * sanitizeHtml — shared HTML sanitizer for every dangerouslySetInnerHTML sink
 * that renders user/guest/vendor-derived or message/template content.
 *
 * Purpose: prevent stored-XSS. Any HTML that originates from data the host,
 * a guest, a vendor, or a template author can influence MUST pass through this
 * before being injected via dangerouslySetInnerHTML.
 *
 * ── Implementation ──
 * Primary sanitizer is DOMPurify (a project dependency — see package.json).
 * sanitizeHtml() calls DOMPurify.sanitize() with its safe default config,
 * which preserves legitimate email/template HTML (<table>, <img>, inline
 * style=) while stripping scripts, event handlers, and unsafe URL schemes.
 *
 * If DOMPurify is ever unavailable at runtime (e.g. a non-DOM/SSR context),
 * sanitizeHtml() falls back to the conservative built-in fallbackSanitize()
 * below, which strips the high-risk XSS vectors (script/style/iframe/object/
 * embed/link/meta/base elements, on* event-handler attributes, and
 * javascript:/vbscript:/data: URLs in href/src) so a sink is never fed raw,
 * unsanitized HTML. The fallback is intentionally conservative and is NOT a
 * full substitute for DOMPurify — it exists only as a safety net.
 */

import DOMPurify from 'dompurify';

const EMPTY = '';

/**
 * Conservative fallback sanitizer (used until DOMPurify is installed).
 * Removes dangerous elements, inline event handlers, and unsafe URL schemes.
 * @param {string} html
 * @returns {string}
 */
function fallbackSanitize(html) {
  if (html == null) return EMPTY;
  let out = String(html);

  // Drop entire dangerous element blocks (including their content).
  out = out.replace(
    /<\s*(script|style|iframe|object|embed|link|meta|base)\b[\s\S]*?<\s*\/\s*\1\s*>/gi,
    ''
  );
  // Drop self-closing / unclosed dangerous tags too.
  out = out.replace(
    /<\s*(script|style|iframe|object|embed|link|meta|base)\b[^>]*\/?\s*>/gi,
    ''
  );
  // Strip inline event handlers: on*="..." / on*='...' / on*=value
  out = out.replace(/\s+on[a-z0-9_-]+\s*=\s*"[^"]*"/gi, '');
  out = out.replace(/\s+on[a-z0-9_-]+\s*=\s*'[^']*'/gi, '');
  out = out.replace(/\s+on[a-z0-9_-]+\s*=\s*[^\s>]+/gi, '');
  // Neutralize javascript: / vbscript: / data: URLs in href/src/xlink:href.
  out = out.replace(
    /(\b(?:href|src|xlink:href)\s*=\s*)("|')\s*(?:javascript|vbscript|data)\s*:[^"']*\2/gi,
    '$1$2#$2'
  );
  out = out.replace(
    /(\b(?:href|src|xlink:href)\s*=\s*)(?:javascript|vbscript|data)\s*:[^\s>]*/gi,
    '$1#'
  );
  return out;
}

/**
 * Sanitize an HTML string for safe injection via dangerouslySetInnerHTML.
 * @param {string} html  raw, possibly-untrusted HTML
 * @returns {string}     sanitized HTML
 */
export function sanitizeHtml(html) {
  if (html == null) return EMPTY;
  // DOMPurify is the primary sanitizer. If it is somehow unavailable at
  // runtime (e.g. a non-DOM environment), fall back to the conservative
  // built-in stripper so a sink is never fed raw, unsanitized HTML.
  try {
    if (DOMPurify && typeof DOMPurify.sanitize === 'function') {
      return DOMPurify.sanitize(String(html));
    }
  } catch (_e) { /* fall through to the conservative fallback */ }
  return fallbackSanitize(html);
}

export default sanitizeHtml;
